import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import { Avatar, Button, Icon, Switch, Tooltip } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { DoctorInterface, OrganizationMember } from '@/entities/organization';

import styles from '../ui/StaffMembers/StaffMembers.module.scss';

type UseGetColumnsSchemeParams = {
  canEditMFA: boolean;
  isMFARequiredForEveryone: boolean;
  isMFASettingsEnabled: boolean;
  onRemoveMember: (userID: string) => void;
  onToggleOrganizationMFA: (required: boolean) => void;
  onToggleStaffMFA: (staffID: string, required: boolean) => void;
};

export const useGetColumnsScheme = (params: UseGetColumnsSchemeParams) => {
  const {
    canEditMFA,
    isMFARequiredForEveryone,
    isMFASettingsEnabled,
    onRemoveMember,
    onToggleOrganizationMFA,
    onToggleStaffMFA,
  } = params;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const handleOpenPermissionsModal = useCallback(() => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.PermissionsModal,
      }),
    );
  }, []);

  const handleOpenEditInfoModal = useCallback(
    (user: OrganizationMember) => {
      const editingUserID = user?.UserID ?? '';

      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.AddStaffModal,
          data: {
            actionType: 'Edit',
            editingUserID,
            doctorInfo: user,
          },
        }),
      );
    },
    [dispatch],
  );

  const columnHelper = createColumnHelper<DoctorInterface>();

  const columnsScheme = [
    columnHelper.accessor(
      (row) => ({
        fullName: row.fullName,
        avatarSrc: row.avatarSrc,
        initials: row.initials,
      }),
      {
        id: 'fullName',
        enableSorting: false,
        sortingFn: 'auto',
        minSize: 180,
        header: formatMessage({
          id: 'staffMembers.name',
          defaultMessage: 'Name',
        }),
        cell: (cell) => {
          const { fullName, avatarSrc, initials } = cell.getValue();

          const emptyAvatarImgAlt = formatMessage({
            id: 'staffMembers.emptyAvatar',
            defaultMessage: 'avatar-pic',
          });

          return (
            <div className={styles.rowName}>
              <Avatar
                src={avatarSrc}
                alt={fullName ?? emptyAvatarImgAlt}
                initials={initials}
              />

              <Tooltip.Third
                content={fullName}
                delayDuration={800}
                side="right"
                align="end"
                alignOffset={-12}
              >
                <span
                  className={cn(styles.fullName, styles.forWhiteSpace, 'p2')}
                >
                  {fullName}
                </span>
              </Tooltip.Third>
            </div>
          );
        },
      },
    ),
    columnHelper.accessor((row) => row.email, {
      id: 'email',
      enableSorting: false,
      sortingFn: 'auto',
      minSize: 240,
      header: formatMessage({
        id: 'staffMembers.email',
        defaultMessage: 'Email',
      }),
      cell: (cell) => {
        const email = cell.getValue();
        const hasUserID = Boolean(cell.row.original.id);

        const notRegisteredLabel = (
          <span className={styles.notRegistered}>
            {formatMessage({
              id: 'global.notRegistered',
              defaultMessage: 'Not registered',
            })}
          </span>
        );

        return (
          <div className={cn(styles.infoInCell, styles.emailCell)}>
            {email && (
              <Tooltip.Third
                content={email}
                delayDuration={800}
                side="right"
                align="end"
                alignOffset={-12}
              >
                <span className={styles.forWhiteSpace}>{email}</span>
              </Tooltip.Third>
            )}
            {!hasUserID && notRegisteredLabel}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.specializations, {
      id: 'specializations',
      enableSorting: false,
      sortingFn: 'auto',
      minSize: 150,
      header: formatMessage({
        id: 'staffMembers.specialization',
        defaultMessage: 'Specialization',
      }),
      cell: (cell) => {
        const specializations = cell.getValue();

        return (
          <div className={cn(styles.infoInCell, styles.textColumn)}>
            {specializations.map((specialization) => (
              <span key={specialization}>{specialization}</span>
            ))}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.roles, {
      id: 'roles',
      enableSorting: false,
      sortingFn: 'auto',
      minSize: 160,
      header: () => (
        <div className={styles.permissionsCol}>
          <p className="p2">
            <FormattedMessage
              id="staffMembers.permissions"
              defaultMessage="Access level"
            />
          </p>

          <Icon
            name="info"
            className={cn(styles.icon, styles.infoIcon)}
            size={24}
            onClick={handleOpenPermissionsModal}
          />
        </div>
      ),
      cell: (cell) => {
        const roles = cell.getValue();

        return (
          <div className={cn(styles.infoInCell, styles.textColumn)}>
            {roles.map((role) => (
              <span key={role}>{role}</span>
            ))}
          </div>
        );
      },
    }),
    ...(isMFASettingsEnabled
      ? [
          columnHelper.accessor((row) => row.doctorInfo, {
            id: 'mfa',
            enableSorting: false,
            minSize: 220,
            header: () => (
              <div className={styles.mfaHeader}>
                <p className="p2">
                  <FormattedMessage
                    id="staffMembers.twoStepVerification"
                    defaultMessage="Two-step verification"
                  />
                </p>

                <Switch
                  checked={isMFARequiredForEveryone}
                  disabled={!canEditMFA}
                  labelClassName={styles.mfaSwitchLabel}
                  onChange={(event) =>
                    onToggleOrganizationMFA(event.target.checked)
                  }
                >
                  <FormattedMessage
                    id="staffMembers.requireForEveryone"
                    defaultMessage="Require for everyone"
                  />
                </Switch>
              </div>
            ),
            cell: (cell) => {
              const doctorInfo = cell.getValue();
              const isRequiredForMember =
                isMFARequiredForEveryone || (doctorInfo?.MFARequired ?? false);

              return (
                <div className={styles.mfaCell}>
                  <Switch
                    checked={isRequiredForMember}
                    disabled={
                      !canEditMFA ||
                      isMFARequiredForEveryone ||
                      cell.row.original.isDeleted ||
                      !doctorInfo
                    }
                    onChange={(event) => {
                      if (doctorInfo) {
                        onToggleStaffMFA(doctorInfo.ID, event.target.checked);
                      }
                    }}
                  />

                  <span className={cn(styles.mfaStatus, 'p2')}>
                    {isRequiredForMember ? (
                      <FormattedMessage
                        id="staffMembers.mfaRequired"
                        defaultMessage="Required"
                      />
                    ) : (
                      <FormattedMessage
                        id="staffMembers.mfaNotRequired"
                        defaultMessage="Not required"
                      />
                    )}
                  </span>
                </div>
              );
            },
          }),
        ]
      : []),
    columnHelper.accessor((row) => row.doctorInfo, {
      id: 'delete',
      enableSorting: false,
      size: 62,
      minSize: 62,
      header: '',
      cell: (cell) => {
        const doctorInfo = cell.getValue();
        const isDeleted = cell.row.original.isDeleted;

        return (
          <div className={styles.actions}>
            <Button
              variant="tertiary"
              className={cn(styles.icon, styles.editIcon)}
              disabled={isDeleted}
              onClick={() => {
                if (doctorInfo) {
                  handleOpenEditInfoModal(doctorInfo);
                }
              }}
              icon="pen"
              size="medium"
            />

            <Button
              className={cn(styles.icon, styles.deleteIcon)}
              variant="tertiary"
              disabled={isDeleted}
              onClick={() => {
                if (doctorInfo) {
                  onRemoveMember(doctorInfo.ID);
                }
              }}
              icon="delete"
              size="medium"
            />
          </div>
        );
      },
    }),
  ];

  return columnsScheme;
};
